import React from "react";
import AiUsageMeter from "./AiUsageMeter";

/**
 * Left-hand guidance for the AI Assistant. The conversation itself (question box, grounded answer, evidence,
 * summaries, report) is the embedded ChatSearch component in the results area.
 */
export default function AiAssistantPanel({
  entitlement,
  hasSession,
  onNewConversation,
  question,
  onQuestionChange,
  onAsk,
  isLoading,
  conversation,
}) {
  const canAsk = Boolean(question && question.trim()) && !isLoading;
  const asked = Array.isArray(conversation) ? conversation : [];
  return (
    <form
      className="ds-panel ds-criteria"
      id="aiAssistantControls"
      autoComplete="off"
      onSubmit={(event) => {
        event.preventDefault();
        if (canAsk) onAsk();
      }}
    >
      <div className="ds-panel-heading">
        <h2>AI Assistant</h2>
        <p>
          Ask a question in plain language. The answer is grounded in the
          documents in scope, with citations you can open in the viewer. Once
          results are shown you can ask follow-up questions, summarize selected
          documents or generate a report from them.
        </p>
      </div>

      <div className="ds-field">
        <label htmlFor="assistantQuestion">Your question</label>
        <textarea
          id="assistantQuestion"
          className="ds-textarea"
          rows={3}
          value={question || ""}
          placeholder="Ask a question about your documents"
          onChange={(event) => onQuestionChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && canAsk) {
              event.preventDefault();
              onAsk();
            }
          }}
        />
        <p className="ds-hint">
          {hasSession
            ? "Asks a new search. To ask about the records on the right, use the follow-up box under them."
            : "Finds the records that answer it; then ask follow-ups about them."}
        </p>
      </div>

      <div className="ds-actions">
        <button type="submit" className="btn ds-btn-primary searchResetbutton" disabled={isLoading}>
          {isLoading ? "Asking..." : "Ask"}
        </button>
        <button
          type="button"
          className="btn ds-btn-secondary searchResetbutton"
          disabled={!hasSession}
          onClick={onNewConversation}
        >
          New conversation
        </button>
      </div>

      {asked.length > 0 && (
        <div className="ds-conversation" aria-label="This conversation">
          <span className="ds-field-label">This conversation</span>
          <ol>
            {asked.map((item, index) => (
              <li key={`${index}-${item}`}>You · {item}</li>
            ))}
          </ol>
        </div>
      )}

      <ol className="ds-steps">
        <li>
          <strong>Ask</strong> — the assistant finds the documents that answer
          the question.
        </li>
        <li>
          <strong>Follow up</strong> — ask about those results only; every
          claim cites a passage.
        </li>
        <li>
          <strong>Summarize or report</strong> — up to four documents at a time,
          or a report across the whole result set.
        </li>
      </ol>

      <AiUsageMeter entitlement={entitlement} />
    </form>
  );
}
